import { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import styles from "./analytics.module.css";

const CHART_COLORS = {
  text: "#5a6577",
  grid: "rgba(26, 43, 95, 0.1)",
};

const toLabelDate = (label) => {
  if (!label) return null;
  const date = new Date(`${label}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

export default function AnalyticsGrowthCharts({
  growthSeries,
  monthlyGrowth,
  assetsSeries,
}) {
  const growthChartRef = useRef(null);
  const incrementalChartRef = useRef(null);
  const assetsChartRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const rawLabels = growthSeries?.labels || [];
    const rawValues = (growthSeries?.values || []).map((value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    });
    const monthlyMap = new Map();
    for (let i = 0; i < rawLabels.length; i++) {
      const date = toLabelDate(rawLabels[i]);
      const value = rawValues[i];
      if (!date || value == null) continue;
      const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
      const entry = monthlyMap.get(key);
      if (!entry || date > entry.date) {
        monthlyMap.set(key, { date, value });
      }
    }
    const monthlyEntries = Array.from(monthlyMap.values()).sort(
      (a, b) => a.date - b.date
    );
    const labels = monthlyEntries.map((entry) =>
      entry.date.toISOString().slice(0, 10)
    );
    const values = monthlyEntries.map((entry) => entry.value);
    // Chart uses monthly points; correct only the first if still the bad inception reading.
    if (values.length > 0 && values[0] < 0.5) {
      values[0] = 1;
    }
    const monthlyPointRadius = [];
    const monthlyPointBackground = [];
    const monthlyPointBorder = [];
    const monthlyPointHitRadius = [];
    for (let i = 0; i < labels.length; i++) {
      monthlyPointRadius.push(3);
      monthlyPointBackground.push("#5BA4E6");
      monthlyPointBorder.push("#5BA4E6");
      monthlyPointHitRadius.push(8);
    }

    const canvas = document.getElementById("growthMetricsChart");
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    if (growthChartRef.current) {
      const chart = growthChartRef.current;
      chart.data.labels = labels;
      if (chart.data.datasets[0]) {
        chart.data.datasets[0].data = values;
        chart.data.datasets[0].pointRadius = monthlyPointRadius;
        chart.data.datasets[0].pointHoverRadius = 6;
        chart.data.datasets[0].pointBackgroundColor = monthlyPointBackground;
        chart.data.datasets[0].pointBorderColor = monthlyPointBorder;
        chart.data.datasets[0].borderWidth = 1.5;
        chart.data.datasets[0].pointHitRadius = monthlyPointHitRadius;
      }
      chart.update("none");
      return;
    }

    growthChartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Growth of $1",
            data: values,
            borderColor: "#5BA4E6",
            backgroundColor: "rgba(91, 164, 230, 0.12)",
            fill: true,
            tension: 0.35,
            pointRadius: monthlyPointRadius,
            pointHoverRadius: 6,
            pointBackgroundColor: monthlyPointBackground,
            pointBorderColor: monthlyPointBorder,
            borderWidth: 1.5,
            pointHitRadius: monthlyPointHitRadius,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "nearest",
          intersect: true,
        },
        hover: {
          mode: "nearest",
          intersect: true,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            filter: (context) => {
              const radius = context.dataset.pointRadius;
              if (Array.isArray(radius)) {
                return (radius[context.dataIndex] || 0) > 0;
              }
              return (radius || 0) > 0;
            },
          },
        },
        scales: {
          x: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              maxTicksLimit: 8,
              callback: function (value) {
                const label = this.getLabelForValue(value);
                const date = toLabelDate(label);
                if (!date) {
                  return label;
                }
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                });
              },
            },
          },
          y: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: (value) => Number(value).toFixed(2),
            },
          },
        },
      },
    });
  }, [growthSeries]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const labels = monthlyGrowth?.labels || [];
    const values = (monthlyGrowth?.values || []).map((value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    });
    const colors = values.map((value) =>
      Number.isFinite(value) && value < 0 ? "#E53935" : "#2d8a5f"
    );

    const canvas = document.getElementById("incrementalGrowthChart");
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    if (incrementalChartRef.current) {
      const chart = incrementalChartRef.current;
      chart.data.labels = labels;
      if (chart.data.datasets[0]) {
        chart.data.datasets[0].data = values;
        chart.data.datasets[0].backgroundColor = colors;
      }
      chart.update("none");
      return;
    }

    incrementalChartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Monthly Growth",
            data: values,
            backgroundColor: colors,
            borderRadius: 4,
            minBarLength: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1a2b5f",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            displayColors: false,
            padding: 10,
            callbacks: {
              label: (context) => {
                const value = Number(context.parsed?.y);
                if (!Number.isFinite(value)) {
                  return "";
                }
                const sign = value > 0 ? "+" : "";
                return `${sign}${value.toFixed(2)}%`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              maxTicksLimit: 12,
            },
          },
          y: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: (value) => `${value}%`,
            },
          },
        },
      },
    });
  }, [monthlyGrowth]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const rawLabels = assetsSeries?.labels || [];
    const rawTotalAssets = (assetsSeries?.totalAssets || []).map((value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    });
    const rawUsdiValues = (assetsSeries?.usdi || []).map((value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    });
    const monthlyMap = new Map();
    for (let i = 0; i < rawLabels.length; i++) {
      const date = toLabelDate(rawLabels[i]);
      const totalAssets = rawTotalAssets[i];
      const usdi = rawUsdiValues[i];
      if (!date || totalAssets == null || usdi == null) continue;
      const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
      const entry = monthlyMap.get(key);
      if (!entry || date > entry.date) {
        monthlyMap.set(key, { date, totalAssets, usdi });
      }
    }
    const monthlyEntries = Array.from(monthlyMap.values()).sort(
      (a, b) => a.date - b.date
    );
    const labels = monthlyEntries.map((entry) =>
      entry.date.toISOString().slice(0, 10)
    );
    const totalAssets = monthlyEntries.map((entry) => entry.totalAssets);
    const usdiValues = monthlyEntries.map((entry) => entry.usdi);
    const monthlyPointRadius = [];
    const monthlyPointBackground = [];
    const monthlyPointBorder = [];
    const monthlyPointHitRadius = [];
    for (let i = 0; i < labels.length; i++) {
      monthlyPointRadius.push(3);
      monthlyPointBackground.push("#5BA4E6");
      monthlyPointBorder.push("#5BA4E6");
      monthlyPointHitRadius.push(8);
    }

    const canvas = document.getElementById("assetsGrowthChart");
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    if (assetsChartRef.current) {
      const chart = assetsChartRef.current;
      chart.data.labels = labels;
      if (chart.data.datasets[0]) {
        chart.data.datasets[0].data = totalAssets;
        chart.data.datasets[0].pointRadius = monthlyPointRadius;
        chart.data.datasets[0].pointHoverRadius = 6;
        chart.data.datasets[0].pointBackgroundColor = monthlyPointBackground;
        chart.data.datasets[0].pointBorderColor = monthlyPointBorder;
        chart.data.datasets[0].borderWidth = 1.5;
        chart.data.datasets[0].pointHitRadius = monthlyPointHitRadius;
      }
      if (chart.data.datasets[1]) {
        chart.data.datasets[1].data = usdiValues;
        chart.data.datasets[1].pointRadius = monthlyPointRadius;
        chart.data.datasets[1].pointHoverRadius = 6;
        chart.data.datasets[1].pointBackgroundColor = monthlyPointBackground.map(
          (color) => (color === "transparent" ? "transparent" : "#2d8a5f")
        );
        chart.data.datasets[1].pointBorderColor = monthlyPointBorder.map(
          (color) => (color === "transparent" ? "transparent" : "#2d8a5f")
        );
        chart.data.datasets[1].borderWidth = 1.5;
        chart.data.datasets[1].pointHitRadius = monthlyPointHitRadius;
      }
      chart.update("none");
      return;
    }

    assetsChartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Total Assets",
            data: totalAssets,
            borderColor: "#5BA4E6",
            backgroundColor: "rgba(91, 164, 230, 0.1)",
            fill: true,
            tension: 0.35,
            pointRadius: monthlyPointRadius,
            pointHoverRadius: 6,
            pointBackgroundColor: monthlyPointBackground,
            pointBorderColor: monthlyPointBorder,
            borderWidth: 1.5,
            pointHitRadius: monthlyPointHitRadius,
          },
          {
            label: "USDI in Circulation",
            data: usdiValues,
            borderColor: "#2d8a5f",
            backgroundColor: "transparent",
            fill: false,
            tension: 0.35,
            pointRadius: monthlyPointRadius,
            pointHoverRadius: 6,
            pointBackgroundColor: monthlyPointBackground.map((color) =>
              color === "transparent" ? "transparent" : "#2d8a5f"
            ),
            pointBorderColor: monthlyPointBorder.map((color) =>
              color === "transparent" ? "transparent" : "#2d8a5f"
            ),
            borderWidth: 1.5,
            pointHitRadius: monthlyPointHitRadius,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "nearest",
          intersect: true,
        },
        hover: {
          mode: "nearest",
          intersect: true,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1a2b5f",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            displayColors: true,
            padding: 10,
            filter: (context) => {
              const radius = context.dataset.pointRadius;
              if (Array.isArray(radius)) {
                return (radius[context.dataIndex] || 0) > 0;
              }
              return (radius || 0) > 0;
            },
            callbacks: {
              label: (context) => {
                const value = Number(context.parsed?.y);
                if (!Number.isFinite(value)) {
                  return "";
                }
                return `${context.dataset.label}: ${value.toLocaleString("en-US")}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              maxTicksLimit: 8,
              callback: function (value) {
                const label = this.getLabelForValue(value);
                const date = toLabelDate(label);
                if (!date) {
                  return label;
                }
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                });
              },
            },
          },
          y: {
            grid: { color: CHART_COLORS.grid },
            ticks: {
              color: CHART_COLORS.text,
              font: { size: 10 },
              callback: (value) => {
                const numeric = Number(value);
                if (!Number.isFinite(numeric)) {
                  return value;
                }
                if (numeric >= 1e9) {
                  return `${(numeric / 1e9).toFixed(1)}B`;
                }
                if (numeric >= 1e6) {
                  return `${(numeric / 1e6).toFixed(1)}M`;
                }
                if (numeric >= 1e3) {
                  return `${(numeric / 1e3).toFixed(1)}K`;
                }
                return numeric.toFixed(0);
              },
            },
          },
        },
      },
    });
  }, [assetsSeries]);

  useEffect(() => {
    return () => {
      if (growthChartRef.current) {
        growthChartRef.current.destroy();
        growthChartRef.current = null;
      }
      if (incrementalChartRef.current) {
        incrementalChartRef.current.destroy();
        incrementalChartRef.current = null;
      }
      if (assetsChartRef.current) {
        assetsChartRef.current.destroy();
        assetsChartRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <div className={styles.growthChartsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartTitle}>Dollar Value Growth Over Time</div>
          <div className={styles.chartBody}>
            <canvas id="growthMetricsChart"></canvas>
          </div>
        </div>
        <div className={styles.chartCard}>
          <div className={styles.chartTitle}>Incremental Growth Per Month</div>
          <div className={styles.chartBody}>
            <canvas id="incrementalGrowthChart"></canvas>
          </div>
        </div>
      </div>

      <div className={styles.chartCardFull}>
        <div className={styles.chartTitle}>Total Assets & USDI in Circulation</div>
        <div className={styles.chartBody}>
          <canvas id="assetsGrowthChart"></canvas>
        </div>
        <div className={styles.assetsLegend}>
          <div className={styles.legendItem}>
            <span
              className={styles.assetsLegendDot}
              style={{ backgroundColor: "#5BA4E6" }}
            ></span>
            <span>Total Assets</span>
          </div>
          <div className={styles.legendItem}>
            <span
              className={styles.assetsLegendDot}
              style={{ backgroundColor: "#2d8a5f" }}
            ></span>
            <span>USDI in Circulation</span>
          </div>
        </div>
      </div>

    </>
  );
}
